import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, ButtonVariant, ButtonSize } from '../atoms/button';
import useMavenApi, { MavenApiError } from '../../auth/useMavenApi';

/**
 * "Find my consultations": proves the visitor owns an email they used on imavenai.com, so the
 * consultations given under that email are attached to this account.
 *
 *   email → POST /client/consultations/claim/start
 *     → { claimed, method:'verified-login' }  the email is this account's verified login: done, no code
 *     → { sent, cooldownSeconds, expiresMinutes }  a 6-digit code went to that email
 *   code  → POST /client/consultations/claim/verify → { claimed } | 400 invalid-code / expired / too-many-attempts
 *
 * The server never says whether consultations exist for an email before ownership is proven, so
 * the "code sent" wording must not imply there is anything to find. The email and code live only
 * in this component's state (never in localStorage).
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Box = styled.div`
  background: #ffffff;
  border: 1px solid #dce5f4;
  border-radius: 12px;
  padding: 20px;
  max-width: 560px;
  width: 100%;
  color: #192947;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 1.05em;
  margin-bottom: 6px;
  color: #192947;
`;

const Hint = styled.p`
  margin: 0 0 14px 0;
  font-size: 0.9em;
  line-height: 1.55;
  color: #65748d;
`;

const Row = styled.form`
  display: flex;
  gap: 10px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const Input = styled.input`
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #cad5e9;
  border-radius: 8px;
  color: #192947;
  padding: 10px 12px;
  font-size: 0.95em;
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: rgba(51, 92, 244, 0.8); }
  &::placeholder { color: #a9b5c8; }
  &.code {
    letter-spacing: 0.4em;
    font-size: 1.15em;
    font-variant-numeric: tabular-nums;
    max-width: 220px;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #2c64da;
  font-size: 0.85em;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:disabled {
    color: #8f9db3;
    cursor: default;
    text-decoration: none;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const Message = styled.div`
  margin-top: 12px;
  font-size: 0.875em;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 8px;
  color: ${p => (p.$kind === 'error' ? '#c53030' : p.$kind === 'success' ? '#0f8f63' : '#455776')};
  background: ${p => (p.$kind === 'error' ? 'rgba(220, 47, 47, 0.12)' : p.$kind === 'success' ? 'rgba(18, 165, 113, 0.1)' : '#f5f8fd')};
  border: 1px solid ${p => (p.$kind === 'error' ? 'rgba(220, 47, 47, 0.3)' : p.$kind === 'success' ? 'rgba(18, 165, 113, 0.3)' : '#e0e8f5')};
`;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const claimedMessage = (n, email) => (n > 0
  ? `${plural(n, 'consultation was', 'consultations were')} added to your account from ${email}.`
  : `No consultations were found for ${email}. If you used a different email on imavenai.com, try that one.`);

const verifyErrorMessage = (e) => {
  if (e instanceof MavenApiError) {
    if (e.code === 'invalid-code') return 'That code is not correct. Check the email and try again.';
    if (e.code === 'expired') return 'This code has expired. Send a new one.';
    if (e.code === 'too-many-attempts') return 'Too many wrong attempts. Send a new code and try again.';
  }
  return e?.message || 'Could not check the code. Please try again.';
};

const startErrorMessage = (e) => {
  if (e instanceof MavenApiError && e.status === 429) return 'Please wait a moment before asking for another code.';
  if (e instanceof MavenApiError && e.status === 400) return 'Please enter a valid email address.';
  return e?.message || 'Could not send the code. Please try again.';
};

const FindConsultationsBox = ({ initialEmail = '', title = 'Find my consultations', hint, onClaimed, onCancel }) => {
  const callMaven = useMavenApi();
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { kind: 'error'|'success'|'info', text }
  const [cooldown, setCooldown] = useState(0);
  const [expiresMinutes, setExpiresMinutes] = useState(10);
  const codeRef = useRef(null);

  // A later prefill (e.g. the list loaded and we now know the login email) only applies while untouched.
  const touchedRef = useRef(false);
  useEffect(() => { if (!touchedRef.current) setEmail(initialEmail); }, [initialEmail]);

  // Resend cooldown countdown (the server enforces it too; this only keeps the button honest).
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => { if (step === 'code') codeRef.current?.focus(); }, [step]);

  const trimmedEmail = email.trim();
  const emailValid = EMAIL_RE.test(trimmedEmail);

  const start = async (e) => {
    e?.preventDefault();
    if (!emailValid || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await callMaven('/client/consultations/claim/start', { method: 'POST', body: { email: trimmedEmail } });
      if (r && typeof r.claimed === 'number') {
        // The email is this account's verified login email: claimed directly, no code needed.
        setStep('email');
        setCode('');
        setMessage({ kind: r.claimed > 0 ? 'success' : 'info', text: claimedMessage(r.claimed, trimmedEmail) });
        onClaimed?.(r.claimed, trimmedEmail);
      } else {
        setStep('code');
        setCode('');
        setCooldown(Number(r?.cooldownSeconds) || 30);
        setExpiresMinutes(Number(r?.expiresMinutes) || 10);
        setMessage({ kind: 'info', text: `If ${trimmedEmail} was used on imavenai.com, a 6-digit code is on its way. It expires in ${Number(r?.expiresMinutes) || 10} minutes.` });
      }
    } catch (err) {
      if (err instanceof MavenApiError && err.status === 429) {
        const wait = Number(err.body?.cooldownSeconds || err.body?.retryAfterSeconds) || 30;
        setCooldown(wait);
      }
      setMessage({ kind: 'error', text: startErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await callMaven('/client/consultations/claim/verify', { method: 'POST', body: { email: trimmedEmail, code } });
      const n = Number(r?.claimed) || 0;
      setStep('email');
      setCode('');
      setMessage({ kind: n > 0 ? 'success' : 'info', text: claimedMessage(n, trimmedEmail) });
      onClaimed?.(n, trimmedEmail);
    } catch (err) {
      setCode('');
      // An expired or burned code cannot be retried: let them ask for a new one straight away.
      if (err instanceof MavenApiError && (err.code === 'expired' || err.code === 'too-many-attempts')) setCooldown(0);
      setMessage({ kind: 'error', text: verifyErrorMessage(err) });
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const changeEmail = () => {
    setStep('email');
    setCode('');
    setMessage(null);
  };

  return (
    <Box data-testid="find-consultations">
      <Title>{title}</Title>
      <Hint>
        {hint || 'Enter the email you gave on imavenai.com. We will send a 6-digit code to it, so only the owner of that email can add its consultations to this account.'}
      </Hint>

      {step === 'email' ? (
        <Row onSubmit={start}>
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { touchedRef.current = true; setEmail(e.target.value); }}
            autoComplete="email"
            maxLength={120}
            aria-label="Email used on imavenai.com"
          />
          <Button type="submit" variant={ButtonVariant.SCHEDULE} size={ButtonSize.MD} disabled={!emailValid || busy || cooldown > 0}>
            {busy ? 'Sending…' : cooldown > 0 ? `Wait ${cooldown}s` : 'Send code'}
          </Button>
        </Row>
      ) : (
        <>
          <Row onSubmit={verify}>
            <Input
              ref={codeRef}
              className="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-label="6-digit code"
            />
            <Button type="submit" variant={ButtonVariant.SCHEDULE} size={ButtonSize.MD} disabled={code.length !== 6 || busy}>
              {busy ? 'Checking…' : 'Verify'}
            </Button>
          </Row>
          <Footer>
            <LinkButton type="button" onClick={() => start()} disabled={busy || cooldown > 0}>
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </LinkButton>
            <LinkButton type="button" onClick={changeEmail} disabled={busy}>Use a different email</LinkButton>
            <span style={{ fontSize: '0.8em', color: '#8f9db3' }}>Code sent to {trimmedEmail} · valid {expiresMinutes} min</span>
          </Footer>
        </>
      )}

      {message && <Message $kind={message.kind} role={message.kind === 'error' ? 'alert' : 'status'}>{message.text}</Message>}

      {onCancel && (
        <Footer>
          <LinkButton type="button" onClick={onCancel}>Close</LinkButton>
        </Footer>
      )}
    </Box>
  );
};

export default FindConsultationsBox;
